
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import jsPDF from "jspdf";
import { calculIR as calcIR, calculCotisationsTotal_micro, TAUX_COTISATIONS_BNC_CLASSIQUE, PASS_2026 } from "@/utils/fiscal";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", bg: "#F4F8FC", white: "#FFFFFF",
  muted: "#6B85A3", border: "#D6E4F0", warning: "#F4A261", light: "#EAF2FB",
  lightTeal: "#E8F5F4", danger: "#E63946",
};

const formatEur = (n: number): string => {
  const formatted = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n));
  // Replace narrow no-break space (\u202F) used by fr-FR locale with regular space for jsPDF compatibility
  return formatted.replace(/\u202F/g, "\u00a0") + "\u00a0€";
};

const fmt = (n: number) => formatEur(n);

const TAUX_URSSAF_BNC = TAUX_COTISATIONS_BNC_CLASSIQUE; // ~40% estimation BNC classique
const ABATTEMENT = 0.34;

type LigneDef = {
  num: string;
  label: string;
  description: string;
  placeholder: string;
  readOnly?: boolean;
  badge?: { text: string; color: string };
  tooltip?: string;
  note?: string;
};

const SECTIONS: { title: string; icon: string; lines: LigneDef[] }[] = [
  {
    title: "Cabinet et équipement", icon: "🏠",
    lines: [
      { num: "14", label: "Loyer et charges locatives", description: "Loyer mensuel × 12, charges de copropriété, crédit-bail immobilier", placeholder: "4 800" },
      { num: "15", label: "Collaboration & sous-location de cabinet", description: "Redevance versée à un confrère pour mise à disposition du cabinet équipé, crédit-bail mobilier, location de matériel professionnel. ⚠️ Les loyers de véhicules ne vont pas ici — ils vont en Ligne 23.", placeholder: "1 200" },
      { num: "17", label: "Entretien et réparations", description: "Réparations du cabinet, entretien du matériel — pas les achats de mobilier", placeholder: "500" },
      { num: "20", label: "Chauffage, eau, gaz, électricité", description: "Charges énergétiques — au prorata si cabinet à domicile (surface pro / surface totale)", placeholder: "800" },
      { num: "22", label: "Assurance RC Pro & locaux", description: "Responsabilité civile professionnelle, assurance des locaux et du matériel professionnel", placeholder: "600" },
    ],
  },
  {
    title: "Déplacements et frais courants", icon: "🚗",
    lines: [
      { num: "23", label: "Frais de véhicule", description: "Barème kilométrique OU frais réels — jamais les deux", placeholder: "2 000", badge: { text: "⚠️ Une seule méthode au choix pour toute l'année", color: C.warning }, tooltip: "Barème 2026 : 7CV et plus = 0,697€/km jusqu'à 5 000km" },
      { num: "24", label: "Autres frais de déplacements", description: "Transports en commun, taxis, parking, péages professionnels", placeholder: "300" },
      { num: "27", label: "Fournitures, documentation, téléphone", description: "Fournitures de bureau, livres professionnels, abonnements, téléphone et internet — part pro uniquement", placeholder: "1 200", tooltip: "Téléphone mixte : déduisez 50 à 80% selon votre usage professionnel réel" },
      { num: "26", label: "Congrès, séminaires, repas professionnels", description: "Frais de congrès, repas d'affaires, déplacements pour formations", placeholder: "800", note: "Repas pris seul hors du cabinet : déductibles entre 5,35€ et 20,70€ par repas" },
    ],
  },
  {
    title: "Cotisations sociales et assurances", icon: "💰",
    lines: [
      { num: "25BT", label: "Cotisations obligatoires URSSAF", description: "", placeholder: "", readOnly: true },
      { num: "25", label: "Cotisations CIPAV obligatoires", description: "Retraite de base et complémentaire obligatoires — montant sur votre appel de cotisations CIPAV", placeholder: "2 000" },
      { num: "25bis", label: "Retraite & prévoyance Madelin", description: "Contrats Madelin : retraite complémentaire, prévoyance, mutuelle, perte d'emploi — contrats groupe uniquement", placeholder: "1 500", tooltip: `Plafond déductible retraite = 10% de votre bénéfice + 15% de la fraction entre 1 et 8 PASS. PASS 2026 = ${PASS_2026.toLocaleString("fr-FR")}€. Maximum théorique : 79 029€` },
      { num: "31", label: "Frais bancaires & intérêts d'emprunt", description: "Agios du compte bancaire professionnel, intérêts d'emprunts liés à l'activité", placeholder: "150" },
    ],
  },
  {
    title: "Honoraires, formation et divers", icon: "📚",
    lines: [
      { num: "21", label: "Honoraires professionnels", description: "Expert-comptable, avocat, conseil fiscal lié à votre activité libérale", placeholder: "1 000" },
      { num: "30", label: "Formation, supervision et frais divers", description: "Supervision clinique, formations continues, DPC, analyses personnelles à visée professionnelle, blanchissage tenues", placeholder: "2 500", badge: { text: "✅ Votre supervision clinique est 100% déductible ici", color: C.teal } },
      { num: "28", label: "Frais juridiques & recouvrement", description: "Frais d'huissier, recouvrement d'impayés, actes juridiques liés à l'activité", placeholder: "200" },
      { num: "29", label: "Cotisations syndicales et professionnelles", description: "Syndicat de psychologues, cotisation à l'ordre, association professionnelle", placeholder: "300" },
    ],
  },
];

const ALL_LINES = SECTIONS.flatMap(s => s.lines);
const EDITABLE_LINES = ALL_LINES.filter(l => !l.readOnly);

interface Props {
  caAnnuel: number;
  onChargesSaved?: () => void;
}

export default function Charges2035({ caAnnuel: caAnnuelProp, onChargesSaved }: Props) {
  const { user } = useAuth();
  const { plan } = useSubscription();
  const isPro = plan === "pro";
  const isEssentiel = !isPro;
  const navigateTo = useNavigate();
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [values, setValues] = useState<Record<string, number>>({});
  const [saved, setSaved] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);
  const [mobileRecapOpen, setMobileRecapOpen] = useState(false);
  const [nomPraticien, setNomPraticien] = useState("");
  const [siret, setSiret] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const siretValid = siret === "" || /^\d{14}$/.test(siret);
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [caByYear, setCaByYear] = useState<number>(0);

  // Cleanup: annule tous les timers de sauvegarde si le composant se démonte
  useEffect(() => {
    return () => {
      Object.values(saveTimers.current).forEach(clearTimeout);
    };
  }, []);

  // Load charges data
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await supabase
        .from("depenses_2035")
        .select("*")
        .eq("annee", annee);
      if (error) { if (import.meta.env.DEV) console.error(error); return; }
      const vals: Record<string, number> = {};
      data?.forEach((d: any) => { vals[d.ligne_num] = Number(d.montant); });
      setValues(vals);
    })();
  }, [user, annee]);

  // Load CA from recettes for selected year
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await supabase
        .from("recettes")
        .select("montant, date")
        .gte("date", `${annee}-01-01`)
        .lte("date", `${annee}-12-31`);
      if (error) { if (import.meta.env.DEV) console.error(error); setCaByYear(0); return; }
      const total = (data || []).reduce((s, r) => s + Number(r.montant), 0);
      setCaByYear(total);
    })();
  }, [user, annee]);

  // Use fetched CA for selected year
  const caAnnuel = caByYear;

  // Cotisations BNC classique — estimation ~40% du bénéfice net
  const COTISATION_LINES = ["25BT", "25", "25bis"];
  const chargesHorsCotisations = useMemo(() => {
    return EDITABLE_LINES
      .filter(l => !COTISATION_LINES.includes(l.num))
      .reduce((sum, l) => sum + (values[l.num] || 0), 0);
  }, [values]);
  const beneficeNetBNC = Math.max(0, caAnnuel - chargesHorsCotisations);
  const urssafAuto = Math.round(beneficeNetBNC * TAUX_URSSAF_BNC);

  const handleChange = useCallback((ligneNum: string, libelle: string, val: string) => {
    const n = val === "" ? 0 : Math.max(0, Math.floor(Number(val)));
    if (isNaN(n)) return;
    setValues(prev => ({ ...prev, [ligneNum]: n }));

    if (saveTimers.current[ligneNum]) clearTimeout(saveTimers.current[ligneNum]);
    saveTimers.current[ligneNum] = setTimeout(async () => {
      if (!user) return;
      const { error } = await supabase
        .from("depenses_2035")
        .upsert(
          { user_id: user.id, annee, ligne_num: ligneNum, libelle, montant: n, updated_at: new Date().toISOString() },
          { onConflict: "user_id,annee,ligne_num" }
        );
      if (error) {
        toast({ title: "❌ Erreur de sauvegarde", description: error.message, variant: "destructive" });
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      }
    }, 600);
  }, [user, annee]);

  const handleBulkSave = useCallback(async () => {
    if (!user) return;
    setBulkSaving(true);
    try {
      const rows = EDITABLE_LINES.map(l => ({
        user_id: user.id,
        annee,
        ligne_num: l.num,
        libelle: l.label,
        montant: values[l.num] || 0,
        updated_at: new Date().toISOString(),
      }));
      const { error } = await supabase
        .from("depenses_2035")
        .upsert(rows, { onConflict: "user_id,annee,ligne_num" });
      if (error) throw error;
      toast({ title: "✅ Vos charges ont été sauvegardées", description: "Votre tableau de bord est mis à jour.", duration: 4000 });
      onChargesSaved?.();
    } catch (e: any) {
      toast({ title: "❌ Erreur lors de la sauvegarde", description: "Vérifiez votre connexion et réessayez.", variant: "destructive", duration: 5000 });
    } finally {
      setBulkSaving(false);
    }
  }, [user, annee, values, onChargesSaved]);

  const totalCharges = useMemo(() => {
    return EDITABLE_LINES.reduce((sum, l) => sum + (values[l.num] || 0), 0) + urssafAuto;
  }, [values, urssafAuto]);

  const filledCount = useMemo(() => {
    return EDITABLE_LINES.filter(l => (values[l.num] || 0) > 0).length;
  }, [values]);

  // ---- Unified financial calculations ----
  const financials = useMemo(() => {
    const beneficeBrut = caAnnuel - totalCharges;
    const isZeroCA = caAnnuel === 0;
    const isDeficit = beneficeBrut <= 0 && !isZeroCA;
    const benefice = Math.max(0, beneficeBrut);
    const urssaf = isZeroCA || isDeficit ? 0 : urssafAuto;
    // benefice est déjà net d'URSSAF (urssafAuto inclus dans totalCharges) — pas de double déduction
    const beneficeImposable = benefice;
    const ir = isZeroCA || isDeficit ? 0 : calcIR(beneficeImposable);
    const revenuNet = isZeroCA || isDeficit ? 0 : beneficeImposable - ir;

    // Micro-BNC comparison — base = CA brut (comme fiscal.ts:calculRevenüNet_micro)
    const urssafMicro = calculCotisationsTotal_micro(caAnnuel); // CIPAV 23,4% du CA (23,2% + 0,2% CFP)
    const beneficeMicro = Math.round(caAnnuel * 0.66);     // abattement 34%
    const irMicro = calcIR(beneficeMicro);
    const revenuNetMicro = caAnnuel - urssafMicro - irMicro; // base = CA brut ✅
    const economieBNC = Math.round(revenuNet - revenuNetMicro);

    return { benefice, urssaf: urssafAuto, ir, revenuNet, isZeroCA, isDeficit, beneficeMicro, revenuNetMicro, economieBNC };
  }, [caAnnuel, totalCharges, urssafAuto]);

  const dateFR = () => {
    const d = new Date();
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  };

  // PDF generation
  const generatePDF = () => {
    if (siret && !siretValid) {
      toast({ title: "⚠️ SIRET invalide", description: "Le SIRET doit contenir exactement 14 chiffres.", variant: "destructive" });
      return;
    }

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;
    const mg = 15;
    const cW = W - mg * 2;
    let y = 0;

    // Header
    doc.setFillColor(27, 58, 92);
    doc.rect(0, 0, W, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("PsyGestion", mg, 12);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(126, 200, 192);
    doc.text(`Pré-déclaration 2035 — ${annee}`, mg, 22);
    y = 34;

    // Practitioner info
    if (nomPraticien || siret) {
      doc.setFontSize(10);
      doc.setTextColor(27, 58, 92);
      doc.setFont("helvetica", "bold");
      if (nomPraticien) { doc.text(`Praticienne : ${nomPraticien}`, mg, y); y += 5; }
      if (siret) { doc.text(`SIRET : ${siret}`, mg, y); y += 5; }
      doc.setFont("helvetica", "normal");
      doc.text("Régime fiscal : BNC Classique", mg, y);
      y += 8;
    }

    // Warning
    doc.setFillColor(255, 243, 224);
    doc.roundedRect(mg, y, cW, 10, 2, 2, "F");
    doc.setFontSize(8);
    doc.setTextColor(196, 98, 0);
    doc.text("Document préparatoire — à remettre à votre expert-comptable. Non opposable à l'administration fiscale.", mg + 4, y + 6);
    y += 16;

    // Recettes
    doc.setFillColor(232, 245, 244);
    doc.roundedRect(mg, y, cW, 8, 2, 2, "F");
    doc.setFontSize(10);
    doc.setTextColor(42, 157, 143);
    doc.setFont("helvetica", "bold");
    doc.text("RECETTES", mg + 4, y + 5.5);
    y += 12;
    doc.setFontSize(9);
    doc.setTextColor(27, 58, 92);
    doc.setFont("helvetica", "normal");
    doc.text("Recettes brutes encaissées (Ligne AA)", mg, y);
    doc.setFont("helvetica", "bold");
    doc.text(formatEur(caAnnuel), W - mg, y, { align: "right" });
    y += 10;

    // Zero CA note
    if (financials.isZeroCA) {
      doc.setFillColor(219, 234, 254);
      doc.roundedRect(mg, y, cW, 10, 2, 2, "F");
      doc.setFontSize(8);
      doc.setTextColor(30, 64, 175);
      doc.text(`Aucune recette saisie pour ${annee} — saisissez vos recettes dans l'onglet Saisir`, mg + 4, y + 6);
      y += 14;
    }

    // Dépenses header
    doc.setFillColor(234, 242, 251);
    doc.roundedRect(mg, y, cW, 8, 2, 2, "F");
    doc.setFontSize(10);
    doc.setTextColor(27, 58, 92);
    doc.setFont("helvetica", "bold");
    doc.text("DÉPENSES PROFESSIONNELLES", mg + 4, y + 5.5);
    y += 12;

    // Table header
    doc.setFontSize(7);
    doc.setTextColor(107, 133, 163);
    doc.setFont("helvetica", "bold");
    doc.text("N°", mg, y);
    doc.text("LIBELLÉ", mg + 18, y);
    doc.text("MONTANT", W - mg, y, { align: "right" });
    y += 4;
    doc.setDrawColor(214, 228, 240);
    doc.line(mg, y, W - mg, y);
    y += 3;

    const filledLines = ALL_LINES.filter(l => {
      const m = l.num === "25BT" ? urssafAuto : (values[l.num] || 0);
      return m > 0;
    });

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");

    if (filledLines.length === 0) {
      doc.setTextColor(107, 133, 163);
      doc.setFont("helvetica", "italic");
      doc.text("Aucune charge saisie pour cette année", mg, y);
      y += 6;
    } else {
      filledLines.forEach(l => {
        if (y > 265) { doc.addPage(); y = 20; }
        const m = l.num === "25BT" ? urssafAuto : (values[l.num] || 0);
        doc.setTextColor(27, 58, 92);
        doc.setFont("helvetica", "normal");
        doc.text(`Ligne ${l.num === "25BT" ? "25 (BT)" : l.num}`, mg, y);
        doc.text(l.label.substring(0, 55), mg + 18, y);
        doc.text(formatEur(m), W - mg, y, { align: "right" });
        y += 1.5;
        doc.setDrawColor(234, 242, 251);
        doc.line(mg, y, W - mg, y);
        y += 4;
      });
    }

    // Total
    doc.setFillColor(234, 242, 251);
    doc.roundedRect(mg, y, cW, 8, 1, 1, "F");
    doc.setFontSize(9);
    doc.setTextColor(27, 58, 92);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL CHARGES", mg + 4, y + 5.5);
    doc.text(formatEur(totalCharges), W - mg - 4, y + 5.5, { align: "right" });
    y += 14;

    // Deficit note
    if (financials.isDeficit) {
      doc.setFillColor(255, 243, 224);
      doc.roundedRect(mg, y, cW, 10, 2, 2, "F");
      doc.setFontSize(8);
      doc.setTextColor(196, 98, 0);
      doc.text("Déficit constaté — charges supérieures aux recettes. Aucune cotisation sociale due.", mg + 4, y + 6);
      y += 14;
    }

    // Results block
    const { benefice, ir, revenuNet, economieBNC } = financials;
    // CSG déductible BNC classique : 6,8% du bénéfice net (source ARAPL 2026)
    const csgDeductible = financials.isZeroCA || financials.isDeficit ? 0 : Math.round(beneficeNetBNC * 0.068);
    const blockH = 42 + (caAnnuel > 0 && !financials.isDeficit ? 7 : 0);

    if (y + blockH > 270) { doc.addPage(); y = 20; }

    doc.setFillColor(27, 58, 92);
    doc.roundedRect(mg, y, cW, blockH, 3, 3, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    let ry = y + 7;
    const results: [string, string][] = [
      ["Ce sur quoi vous êtes imposée", formatEur(benefice)],
      ["Cotisations estimées (~40% bénéfice)", formatEur(financials.isZeroCA || financials.isDeficit ? 0 : urssafAuto)],
      ["CSG déductible (bordereau URSSAF)", formatEur(csgDeductible)],
      ["Impôt sur le revenu estimé", formatEur(ir)],
      ["Ce que vous touchez vraiment", formatEur(revenuNet)],
    ];
    results.forEach((r) => {
      doc.setFont("helvetica", "normal");
      doc.text(r[0], mg + 6, ry);
      doc.setFont("helvetica", "bold");
      doc.text(r[1], W - mg - 6, ry, { align: "right" });
      ry += 7;
    });

    // Micro-BNC comparison line
    if (caAnnuel > 0 && !financials.isDeficit) {
      if (economieBNC >= 0) {
        doc.setTextColor(76, 175, 80);
        doc.setFont("helvetica", "bold");
        doc.text(`Économie vs Micro-BNC estimée : ${formatEur(economieBNC)}`, mg + 6, ry);
      } else {
        doc.setTextColor(244, 162, 97);
        doc.setFont("helvetica", "bold");
        doc.text(`Le Micro-BNC serait plus avantageux de ${formatEur(Math.abs(economieBNC))} cette année`, mg + 6, ry);
      }
    }

    // Watermark for essentiel users — diagonal, light gray, on all pages
    if (isEssentiel) {
      const pageCount = doc.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.saveGraphicsState();
        // @ts-ignore — jsPDF GState API
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.setTextColor(27, 58, 92);
        doc.setFontSize(28);
        doc.setFont("helvetica", "bold");
        // Rotate text diagonally across the page center
        const cx = 105;
        const cy = 148;
        doc.text("PSYGESTION GRATUIT — PASSEZ PRO", cx, cy, {
          align: "center",
          angle: 35,
        });
        doc.text("POUR RETIRER CE FILIGRANE", cx, cy + 14, {
          align: "center",
          angle: 35,
        });
        doc.restoreGraphicsState();
      }
    }

    // Footer
    doc.setPage(doc.getNumberOfPages());
    doc.setFontSize(7);
    doc.setTextColor(107, 133, 163);
    doc.setFont("helvetica", "normal");
    doc.text(`Document généré par PsyGestion le ${dateFR()} — Non opposable à l'administration fiscale`, W / 2, 288, { align: "center" });

    doc.save(`PsyGestion_2035_${annee}.pdf`);
  };

  const RecapContent = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><div className="opacity-70 text-xs">CA annuel {annee}</div><div className="font-bold text-lg">{fmt(caAnnuel)}</div></div>
      <div><div className="opacity-70 text-xs">Total charges</div><div className="font-bold text-lg">{fmt(totalCharges)}</div></div>
      <div>
        <div className="opacity-70 text-xs">Ce sur quoi vous êtes imposée</div>
        <div className="font-bold text-lg" style={{ color: "#7EC8C0" }}>{fmt(financials.benefice)}</div>
        {financials.isDeficit && <div className="text-[10px]" style={{ color: C.warning }}>Déficit — charges &gt; recettes</div>}
        {financials.isZeroCA && <div className="text-[10px] opacity-70">Aucune recette saisie</div>}
      </div>
      <div>
        {caAnnuel >= 10000 ? (
          <>
            <div className="opacity-70 text-xs">{financials.economieBNC >= 0 ? "Économie vs Micro-BNC" : "Comparaison Micro-BNC"}</div>
            <div className="font-bold text-lg" style={{ color: financials.economieBNC >= 0 ? "#4CAF50" : C.warning }}>
              {fmt(Math.abs(financials.economieBNC))}
            </div>
            <div className="text-[10px] opacity-70">
              {financials.economieBNC >= 0
                ? `Vous économisez ${fmt(financials.economieBNC)} vs Micro-BNC`
                : "Le Micro-BNC serait plus avantageux cette année — consultez votre expert-comptable"}
            </div>
          </>
        ) : (
          <div className="text-xs italic opacity-60">Continuez à saisir vos recettes et charges au fil de l'année pour obtenir une comparaison fiable 📊</div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ backgroundColor: C.bg }} className="min-h-0">
      <div className="max-w-[720px] mx-auto px-4 py-6 pb-40 md:pb-32">

        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: C.navy }}>
              Mes charges déductibles
              <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: C.teal }}>{annee}</span>
            </h1>
            <p className="text-[13px] mt-1" style={{ color: C.muted }}>
              Saisissez vos charges une fois par an — PsyGestion prépare votre document 2035 pour votre expert-comptable.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {saved && <span className="text-xs px-2 py-1 rounded-full text-white transition-opacity" style={{ backgroundColor: C.teal }}>✅ Sauvegardé</span>}
            <select value={annee} onChange={e => setAnnee(Number(e.target.value))}
              className="text-xs border rounded-lg px-2 py-1.5" style={{ borderColor: C.border, color: C.navy }}>
              {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Practitioner info */}
        <div className="rounded-xl border p-4 shadow-sm mb-4" style={{ backgroundColor: C.white, borderColor: C.border, borderRadius: 12 }}>
          <div className="font-semibold text-sm mb-3" style={{ color: C.navy }}>👤 Informations du praticien <span className="text-xs font-normal" style={{ color: C.muted }}>(facultatif — apparaît sur le PDF)</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: C.muted }}>Nom complet</label>
              <input
                type="text"
                value={nomPraticien}
                onChange={e => setNomPraticien(e.target.value)}
                placeholder="Dr. Marie Dupont"
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: C.border, color: C.navy }}
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block" style={{ color: C.muted }}>
                Numéro SIRET <span className="font-normal">(optionnel — pour le PDF)</span>
              </label>
              <input
                type="text"
                value={siret}
                onChange={e => setSiret(e.target.value.replace(/\D/g, "").slice(0, 14))}
                placeholder="Disponible sur avis-situation-sirene.fr"
                maxLength={14}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: siret && !siretValid ? C.danger : C.border, color: C.navy }}
              />
              {siret && !siretValid && (
                <p className="text-[11px] mt-1" style={{ color: C.danger }}>Le SIRET doit contenir 14 chiffres</p>
              )}
            </div>
          </div>
        </div>

        {/* PDF Button */}
        <div className="flex flex-col items-end mb-4">
          {isEssentiel ? (
            <>
              <div className="relative group">
                <button onClick={generatePDF}
                  className="text-sm font-semibold px-4 py-2 rounded-xl text-white transition-colors hover:opacity-90"
                  style={{ backgroundColor: C.muted }}>
                  📄 Exporter le PDF (avec filigrane)
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 z-50">
                  <div className="rounded-xl p-3 text-xs shadow-lg" style={{ backgroundColor: C.navy, color: C.white }}>
                    <p className="mb-1.5">Le PDF généré contient un filigrane PsyGestion.</p>
                    <button onClick={() => navigateTo("/pricing")} className="font-semibold underline" style={{ color: "#2A9D8F" }}>
                      Passez au Pro pour un export propre →
                    </button>
                    <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-2 h-2" style={{ backgroundColor: C.navy }} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <button onClick={generatePDF}
              className="text-sm font-semibold px-4 py-2 rounded-xl text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: C.teal }}>
              Générer ma pré-déclaration PDF 📄
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium" style={{ color: C.muted }}>{filledCount} lignes renseignées sur {EDITABLE_LINES.length}</span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: C.border }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ backgroundColor: C.teal, width: `${(filledCount / EDITABLE_LINES.length) * 100}%` }} />
          </div>
        </div>

        {/* ── GARANTIE FISCALE ── */}
        {(() => {
          const REGLES = [
            {
              id: "supervision",
              icon: "🧠",
              label: "Formation & supervision",
              tip: "Séances de supervision clinique et formations continues — déductibles à 100% (Ligne 30).",
              ok: (values["30"] ?? 0) >= 600,
              alertMsg: "Non renseignée ou inférieure à 600€ — potentiellement des centaines d'euros déductibles oubliées",
              niveau: "warning" as const,
            },
            {
              id: "prevoyance",
              icon: "🏥",
              label: "Prévoyance Madelin",
              tip: "Protège votre revenu en cas d'arrêt maladie ou d'invalidité. Déductible via contrat Madelin (Ligne 25bis).",
              ok: (values["25bis"] ?? 0) > 0,
              alertMsg: "Aucune prévoyance déclarée — vous exercez sans filet de sécurité",
              niveau: "danger" as const,
            },
            {
              id: "assurance",
              icon: "🛡️",
              label: "Assurance RC professionnelle",
              tip: "Obligatoire pour exercer en libéral — couvre votre responsabilité civile (Ligne 22).",
              ok: (values["22"] ?? 0) > 0,
              alertMsg: "Non renseignée — vérifiez que vous êtes bien assuré(e)",
              niveau: "danger" as const,
            },
            {
              id: "loyer",
              icon: "🏠",
              label: "Cohérence du loyer",
              tip: "Le loyer déduit ne devrait pas dépasser ~30% du CA (part professionnelle uniquement).",
              ok: caAnnuel === 0 || (values["14"] ?? 0) === 0 || (values["14"] ?? 0) < caAnnuel * 0.30,
              alertMsg: "Loyer > 30% du CA — vérifiez la quote-part professionnelle déclarée",
              niveau: "warning" as const,
            },
            {
              id: "completude",
              icon: "📋",
              label: "Complétude du dossier",
              tip: "Un dossier bien renseigné pour une psy libérale comporte au moins 4 catégories de charges.",
              ok: filledCount >= 4,
              alertMsg: "Dossier incomplet — des charges déductibles sont peut-être oubliées",
              niveau: "warning" as const,
            },
          ];

          const score = REGLES.filter(r => r.ok).length;
          const total = REGLES.length;
          const allGood = score === total;
          const scoreColor = allGood ? C.teal : score >= 3 ? C.warning : C.danger;
          const scoreLabel = allGood
            ? "Votre dossier est complet et cohérent"
            : score >= 3
              ? "Quelques points à vérifier"
              : "Dossier incomplet — action requise";

          return (
            <div className="rounded-xl border-2 mb-6 overflow-hidden" style={{ borderColor: scoreColor + "60", backgroundColor: C.white }}>
              {/* En-tête score */}
              <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: scoreColor + "15" }}>
                <div className="flex items-center gap-2">
                  <span className="text-base">🛡️</span>
                  <span className="font-bold text-sm" style={{ color: C.navy }}>Garantie Fiscale</span>
                  <span className="text-[11px]" style={{ color: C.muted }}>— vérification automatique</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-sm" style={{ color: scoreColor }}>{score}/{total}</span>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: scoreColor }}>
                    {allGood ? "✅ Validé" : "⚠️ À vérifier"}
                  </span>
                </div>
              </div>

              {/* Score label */}
              <div className="px-4 pt-2 pb-1 text-xs font-medium" style={{ color: C.muted }}>
                {scoreLabel}
              </div>

              {/* Règles */}
              <div className="px-4 pb-3 space-y-2 mt-1">
                {REGLES.map(r => {
                  const color = r.ok ? C.teal : r.niveau === "danger" ? C.danger : C.warning;
                  return (
                    <div
                      key={r.id}
                      className="flex items-start gap-2.5 p-2.5 rounded-lg"
                      style={{ backgroundColor: color + "12" }}
                    >
                      <span className="text-sm mt-0.5 shrink-0">
                        {r.ok ? "✅" : r.niveau === "danger" ? "🔴" : "⚠️"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-xs" style={{ color: C.navy }}>
                          {r.icon} {r.label}
                        </div>
                        {!r.ok && (
                          <p className="text-[11px] mt-0.5 font-medium" style={{ color }}>
                            {r.alertMsg}
                          </p>
                        )}
                        <p className="text-[10px] mt-0.5" style={{ color: C.muted }}>
                          {r.tip}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message final validé */}
              {allGood && (
                <div className="px-4 pb-3">
                  <div
                    className="text-[11px] font-semibold py-2 px-3 rounded-lg text-center"
                    style={{ backgroundColor: C.lightTeal, color: C.teal }}
                  >
                    Votre dossier fiscal est complet — vous êtes protégée contre les oublis les plus fréquents.
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Sections */}
        {SECTIONS.map(section => (
          <div key={section.title} className="rounded-xl shadow-sm border mb-5 overflow-hidden" style={{ backgroundColor: C.white, borderColor: C.border, borderRadius: 12 }}>
            <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
              <span className="text-lg">{section.icon}</span>
              <span className="font-bold text-sm" style={{ color: C.navy }}>{section.title}</span>
            </div>
            <div className="p-5 space-y-6">
              {section.lines.map(line => {
                const isURSSAF = line.readOnly;
                const val = isURSSAF ? urssafAuto : (values[line.num] || "");
                return (
                  <div key={line.num}>
                    <div className="flex items-center gap-2 mb-1">
                      <label className="font-semibold text-sm" style={{ color: C.navy }}>
                        {line.label}
                        {!["25BT", "25", "25bis"].includes(line.num) && (
                          <span className="ml-2 font-normal" style={{ fontSize: 11, color: C.muted }}>
                            (Ligne {line.num})
                          </span>
                        )}
                      </label>
                      {line.tooltip && (
                        <span className="relative inline-block">
                          <button onClick={() => setTooltipOpen(tooltipOpen === line.num ? null : line.num)}
                            className="text-sm cursor-pointer" title="Info">💡</button>
                          {tooltipOpen === line.num && (
                            <div className="absolute left-6 top-0 z-20 w-64 p-3 rounded-xl shadow-lg text-xs border"
                              style={{ backgroundColor: C.white, borderColor: C.border, color: C.navy }}>
                              {line.tooltip}
                              <button onClick={() => setTooltipOpen(null)} className="block mt-1 text-[10px] underline" style={{ color: C.muted }}>Fermer</button>
                            </div>
                          )}
                        </span>
                      )}
                      {isURSSAF && <span className="text-sm">🔒</span>}
                    </div>
                    {line.description && <p className="text-[13px] mb-2" style={{ color: C.muted }}>{line.description}</p>}
                    {line.badge && (
                      <div className="text-[11px] font-semibold px-2 py-1 rounded-lg inline-block mb-2"
                        style={{ backgroundColor: line.badge.color === C.teal ? C.lightTeal : "#FFF3E0", color: line.badge.color }}>
                        {line.badge.text}
                      </div>
                    )}
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={val}
                        readOnly={isURSSAF}
                        onChange={e => handleChange(line.num, line.label, e.target.value)}
                        placeholder={line.placeholder || "0"}
                        className="w-full rounded-lg border px-3 py-2.5 text-sm pr-8 transition-shadow"
                        style={{
                          borderColor: C.border,
                          color: C.navy,
                          backgroundColor: isURSSAF ? "#F0F4F8" : C.white,
                          outline: "none",
                        }}
                        onFocus={e => { if (!isURSSAF) e.target.style.boxShadow = `0 0 0 2px ${C.teal}40`; }}
                        onBlur={e => { e.target.style.boxShadow = "none"; }}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: C.muted }}>€</span>
                    </div>
                    {isURSSAF && (
                      <>
                        <p className="text-[11px] mt-1" style={{ color: C.teal }}>Estimation calculée automatiquement (~40% du bénéfice net)</p>
                        <p className="text-[11px] mt-0.5 italic" style={{ color: C.muted }}>⚠️ Estimation indicative — vos cotisations réelles sont calculées par l'URSSAF sur votre bénéfice net déclaré. Consultez urssaf.fr pour votre montant exact.</p>
                      </>
                    )}
                    {line.note && <p className="text-[11px] mt-1 italic" style={{ color: C.muted }}>{line.note}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Bouton Sauvegarder */}
        <button
          onClick={handleBulkSave}
          disabled={bulkSaving}
          className="w-full font-bold text-white text-sm flex items-center justify-center gap-2 transition-colors hover:opacity-90 disabled:opacity-60"
          style={{
            backgroundColor: C.teal,
            height: 48,
            borderRadius: 12,
          }}
        >
          {bulkSaving ? (
            <>
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              Sauvegarde en cours...
            </>
          ) : (
            <>💾 Sauvegarder mes charges</>
          )}
        </button>
      </div>

      {/* Desktop sticky recap */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0 z-20">
        <div className="max-w-[720px] mx-auto px-4 pb-4">
          <div className="rounded-xl p-4 shadow-xl" style={{ backgroundColor: C.navy, color: "white" }}>
            <RecapContent />
          </div>
        </div>
      </div>

      {/* Mobile sticky save + recap */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-20">
        <div className="px-4 pb-2 pt-2" style={{ backgroundColor: C.bg }}>
          <button
            onClick={handleBulkSave}
            disabled={bulkSaving}
            className="w-full font-bold text-white text-sm flex items-center justify-center gap-2 transition-colors hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: C.teal, height: 48, borderRadius: 12 }}
          >
            {bulkSaving ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                Sauvegarde en cours...
              </>
            ) : (
              <>💾 Sauvegarder mes charges</>
            )}
          </button>
        </div>
        <div className="shadow-xl" style={{ backgroundColor: C.navy, color: "white" }}>
          <button onClick={() => setMobileRecapOpen(!mobileRecapOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold">
            <span>Bénéfice : {fmt(financials.benefice)}</span>
            <span className="transition-transform" style={{ transform: mobileRecapOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▲</span>
          </button>
          {mobileRecapOpen && <div className="px-4 pb-4"><RecapContent /></div>}
        </div>
      </div>
    </div>
  );
}
